// Forwarding a message, like Discord's: pick up to five of the server's text channels and DMs (searchable), add an
// optional note, send. Each pick gets its own forward (a new message by me showing the original as a forwarded block;
// see docs/forwarding.md). A modal on desktop, a bottom sheet on phones.
import { useEffect, useMemo, useState } from "react";
import { MAX_FORWARD_TARGETS, MAX_MESSAGE_LENGTH, type Channel } from "@bloom/shared";
import { isNarrow, isTouch } from "../mobile.ts";
import { Sheet } from "./Sheet.tsx";
import { CloseButton } from "./icons.tsx";
import "./dms.css";
import "./forward.css";

export interface ForwardTarget {
  channel: Channel;
  /** "general", or the other people's names for a DM. */
  label: string;
  /** Category name, or "Direct message". */
  group?: string;
}

/** 0: starts with the query; 1: a word starts with it; 2: contains it; null: no match (as in the quick switcher). */
function score(label: string, q: string): number | null {
  const l = label.toLowerCase();
  if (l.startsWith(q)) return 0;
  if (l.split(/[\s,_-]+/).some((w) => w.startsWith(q))) return 1;
  return l.includes(q) ? 2 : null;
}

export function ForwardDialog({ targets, preview, onSend, onClose }: {
  /** In sidebar order. */
  targets: ForwardTarget[];
  /** The message being forwarded, as one line of plain text (or what it holds: "2 attachments"). */
  preview: string;
  onSend: (channelIds: string[], note: string) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [note, setNote] = useState("");
  const sheet = isNarrow();
  const full = picked.length >= MAX_FORWARD_TARGETS;

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/^[#@]/, "");
    if (!q) return targets;
    return targets
      .map((t, order) => ({ t, order, s: score(t.label, q) }))
      .filter((r): r is { t: ForwardTarget; order: number; s: number } => r.s !== null)
      .sort((a, b) => a.s - b.s || a.order - b.order)
      .map((r) => r.t);
  }, [targets, query]);

  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : p.length >= MAX_FORWARD_TARGETS ? p : [...p, id]));
  const send = () => {
    if (!picked.length) return;
    onSend(picked, note.trim());
  };

  // Desktop: Esc closes (the phone sheet handles its own).
  useEffect(() => {
    if (sheet) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [sheet]);

  const body = (
    <div className="dm-picker fwd-picker">
      <div className="dm-picker-head">
        <h2 id="fwd-title">Forward to</h2>
        {!sheet && <CloseButton onClick={onClose} aria-label="Close" />}
      </div>
      <p className="fwd-preview" title={preview}>
        <ForwardIcon size={14} />
        <span>{preview}</span>
      </p>
      <div className="dm-picker-search">
        <input
          className="dm-picker-input"
          placeholder="Search channels and DMs"
          aria-label="Search channels and DMs"
          value={query}
          autoFocus={!isTouch()}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            e.preventDefault();
            // Enter with a search picks the best match; with an empty box it sends.
            if (query.trim() && matches[0]) {
              if (!picked.includes(matches[0].channel.id)) toggle(matches[0].channel.id);
              setQuery("");
            } else send();
          }}
        />
      </div>
      <div className="dm-picker-list sheet-scroll" role="listbox" aria-multiselectable="true" aria-labelledby="fwd-title">
        {matches.map((t) => {
          const on = picked.includes(t.channel.id);
          return (
            <button key={t.channel.id} type="button" role="option" aria-selected={on} className={`dm-option fwd-option${on ? " on" : ""}`} disabled={!on && full} onClick={() => toggle(t.channel.id)}>
              <span className="fwd-glyph" aria-hidden="true">{t.channel.kind === "dm" ? "@" : "#"}</span>
              <span className="dm-option-name">{t.label}</span>
              {t.group && <span className="fwd-group">{t.group}</span>}
              <span className="dm-check" aria-hidden="true">{on ? "✓" : ""}</span>
            </button>
          );
        })}
        {!matches.length && <p className="muted fwd-none">Nothing by that name.</p>}
      </div>
      <div className="fwd-note">
        <input
          className="input"
          placeholder="Add an optional message…"
          aria-label="Add an optional message"
          value={note}
          maxLength={MAX_MESSAGE_LENGTH}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              send();
            }
          }}
        />
      </div>
      <div className="dm-picker-foot">
        <span className="muted" style={{ fontSize: 12, flex: 1 }}>{full ? `Up to ${MAX_FORWARD_TARGETS} at once.` : picked.length ? `${picked.length} selected` : ""}</span>
        <button className="btn btn-primary btn-small dm-go fwd-send" disabled={!picked.length} onClick={send}>
          Send
        </button>
      </div>
    </div>
  );

  return sheet ? (
    <Sheet label="Forward to" onClose={onClose}>{body}</Sheet>
  ) : (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal dm-modal" role="dialog" aria-modal="true" aria-labelledby="fwd-title">{body}</div>
    </div>
  );
}

/** Discord's forward arrow. */
export function ForwardIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m15 14 5-5-5-5" />
      <path d="M20 9H9.5A5.5 5.5 0 0 0 4 14.5V20" />
    </svg>
  );
}
